// kp-Epic 2 (kennelplanning-migration): bearer-aware avatar fetch for the floorplan/list/pending
// tiles. The access token is in memory (not a cookie), so a plain `<img src>` cannot carry it (D5 /
// AR8). We fetch → blob → object URL → `<img src={objectUrl}>`, with a per-dier cache so the same
// dier isn't re-fetched. Mirrors `src/api/animalPhoto.ts` / `src/api/media.ts`.
//
// The bytes come from the animal photo endpoint the rest of the SPA uses. The planning board first
// asked `GET /api/kennelplanning/animals/{dierId}/avatar` — a route that was never migrated, so every
// call 404’d and the whole board fell back to initials while acc showed photos.
import { getAccessToken } from "../auth";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

// Per-dier object-URL cache shared across tile components. A 404 (no DB image) is cached as `null`
// so the avatar component can fall through to onlineDierAfbeeldingUrl / initials without re-fetching.
const cache = new Map<number, Promise<string | null>>();

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * Resolve the authenticated avatar bytes for a dier as an object URL, or `null` when the backend has
 * no DB image (404) or the fetch fails. Cached per dierId for the lifetime of the page.
 */
export function fetchKennelAvatarObjectUrl(dierId: number): Promise<string | null> {
  const existing = cache.get(dierId);
  if (existing) return existing;
  const promise = (async (): Promise<string | null> => {
    try {
      const res = await fetch(`${API_URL}/animals/${dierId}/photo/content`, {
        headers: authHeaders(),
      });
      if (!res.ok) return null;
      const blob = await res.blob();
      if (blob.size === 0) return null;
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  })();
  cache.set(dierId, promise);
  return promise;
}
